"use client";

import { Check, Loader, Mail } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="mt-8 rounded-lg border border-primary/30 bg-primary/10 p-6 text-center">
        <p className="font-default text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/40 bg-transparent text-foreground hover:bg-primary/10">
          Download another deck
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="mt-8 space-y-4">
      <div className="flex flex-col gap-3 rounded-lg border border-foreground/15 bg-foreground/5 p-2 backdrop-blur-sm sm:flex-row sm:items-center sm:rounded-lg sm:p-1.5">
        <FormField control={control} name="first_name" render={({
        field
      }) => <FormItem className="flex-1">
              <FormControl>
                <Input placeholder="Name" className="h-11 border-0 bg-transparent px-4 text-foreground placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:ring-offset-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="hidden h-6 w-px bg-foreground/15 sm:block" />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email"
        }
      }} render={({
        field
      }) => <FormItem className="flex-[1.5]">
              <FormControl>
                <Input type="email" placeholder="Work email" className="h-11 border-0 bg-transparent px-4 text-foreground placeholder:text-foreground/40 focus-visible:ring-0 focus-visible:ring-offset-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <Button type="submit" disabled={isSubmitting} className="h-11 rounded-lg bg-primary px-6 font-medium text-white hover:bg-primary/90">
          {isSubmitting ? <>
              <Loader className="mr-2 size-4 animate-spin" />
              Sending...
            </> : <>Download <span aria-hidden="true">→</span></>}
        </Button>
      </div>

      <FormField control={control} name="consent_updates" rules={{
      required: "Please confirm to receive updates"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <label className="flex cursor-pointer items-start gap-3 text-left">
                <input type="checkbox" checked={field.value || false} onChange={e => field.onChange(e.target.checked)} className="mt-1 size-4 cursor-pointer accent-primary" />
                <span className="font-default text-sm text-foreground/70">
                  I agree to receive creative insights and agency updates from Vivid Studio.
                </span>
              </label>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}
    </Form>;
}
export default function SecureTheDeck() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    id: 1,
    label: "Full capabilities"
  }, {
    id: 2,
    label: "Case studies"
  }, {
    id: 3,
    label: "Process breakdown"
  }];
  return <section data-section-id="498" id="secure-the-deck" className="relative overflow-hidden bg-background py-24 text-foreground sm:py-32 lg:py-40">
      <div className="absolute inset-0 bg-cover bg-center opacity-15 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701998/img/vivid_studio_bg.png)]" aria-hidden="true" />

      <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" aria-hidden="true" />

      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <span className="absolute left-[12%] top-[20%] size-1 animate-pulse rounded-lg bg-primary/70 [animation-delay:0s] [animation-duration:3s]" />
        <span className="absolute left-[78%] top-[15%] size-1.5 animate-pulse rounded-lg bg-primary/50 [animation-delay:1s] [animation-duration:4s]" />
        <span className="absolute left-[25%] top-[70%] size-1 animate-pulse rounded-lg bg-primary/60 [animation-delay:1.5s] [animation-duration:3.5s]" />
        <span className="absolute left-[88%] top-[60%] size-1 animate-pulse rounded-lg bg-primary/70 [animation-delay:0.5s] [animation-duration:5s]" />
        <span className="absolute left-[55%] top-[85%] size-1.5 animate-pulse rounded-lg bg-primary/40 [animation-delay:2s] [animation-duration:4.5s]" />
        <span className="absolute left-[40%] top-[10%] size-1 animate-pulse rounded-lg bg-primary/50 [animation-delay:2.5s] [animation-duration:3s]" />
        <span className="absolute left-[8%] top-[55%] size-1 animate-pulse rounded-lg bg-primary/60 [animation-delay:0.8s] [animation-duration:4s]" />
        <span className="absolute left-[65%] top-[40%] size-1 animate-pulse rounded-lg bg-primary/50 [animation-delay:1.8s] [animation-duration:3.8s]" />
      </div>

      <div className="relative mx-auto max-w-[720px] px-6">
        <div ref={ref} className={`text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <div className="mb-8 flex items-center justify-center gap-2.5">
            <span className="relative flex size-2">
              <span className="absolute inline-flex size-full animate-ping rounded-lg bg-primary opacity-75" />
              <span className="relative inline-flex size-2 rounded-lg bg-primary" />
            </span>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">
              Capabilities · 2024
            </span>
          </div>

          <h2 className="font-default text-4xl font-medium leading-[1.1] tracking-tight text-foreground sm:text-5xl lg:text-[52px]">
            Download our creative deck
          </h2>

          <p className="mx-auto mt-6 max-w-xl font-default text-lg leading-relaxed text-foreground/70">
            Explore our latest work, strategic methodologies, and production capabilities. See how Vivid Studio transforms concepts into high-impact visual experiences.
          </p>

          <FormProvider formKey="deck_download_form_home" formId="deck_download_form_home" sectionName="secure-the-deck" defaultValues={{
          first_name: "",
          email: "",
          consent_updates: false
        }} validationRules={{
          email: {
            required: true,
            pattern: "email"
          },
          consent_updates: {
            required: true
          }
        }} submitText="Download" successMessage="Success! Your download link is on its way to your inbox." errorMessage="Something went wrong. Please try again.">
            <NewsletterForm />
          </FormProvider>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {benefits.map((benefit, i) => <div data-index={i} key={benefit.id} className="flex items-center gap-2">
                <span className="flex size-4 items-center justify-center rounded-lg bg-primary/15">
                  <Check className="size-3 text-primary" strokeWidth={2.5} />
                </span>
                <span className="font-mono text-xs uppercase tracking-wider text-foreground/60">
                  {benefit.label}
                </span>
              </div>)}
          </div>

          <div className="mt-12 flex items-center justify-center gap-3 text-foreground/30">
            <span className="h-px w-12 bg-foreground/20" />
            <Mail className="size-4" />
            <span className="h-px w-12 bg-foreground/20" />
          </div>
        </div>
      </div>
    </section>;
}